<template>
    <ShipFilter v-model="filters" :has-active-filters="hasActiveFilters" @submit="loadData()" @reset="resetFilters" />
    <v-data-table-server
        :headers="useHeaders"
        :items="ships"
        :items-per-page-options="useTableOptions(totalCount)"
        :options="tableOptions"
        :loading="isLoading"
        :items-length="totalCount"
        disable-sort
        density="compact"
        class="elevation-1 table-server"
        data-cy="ships-table"
        @update:options="onUpdateTableOptions"
    >
        <template v-slot:item.name="{item}">
            <div class="text-link" :data-cy="`ship-${item.id}`" @click="emit('associateShip', item)">
                {{ item.name }}
            </div>
        </template>
        <template v-slot:item.type="{item}">
            {{ item.type && $t(`cruises.ship_types.${item.type}`) }}
        </template>
    </v-data-table-server>
</template>

<script setup lang="ts">
    import {initShipFilter} from '~/utils/types/account/products/helpers'
    import type {ShipFilterType} from '~/utils/types/account/products/types'
    import ShipFilter from '~/components/products/cruises/ShipFilter.vue'

    const emit = defineEmits<{
        (e: 'associateShip', value): void
    }>()

    const isLoading = ref<boolean>(false)
    const ships = ref<any[]>([])
    const totalCount = ref<number>(0)
    const tableOptions = ref({
        itemsPerPage: 10,
        page: 1,
    })
    const filters = ref<ShipFilterType>(initShipFilter())

    const app = useNuxtApp()
    const {t} = useI18n()

    async function loadData(): Promise<void> {
        const {itemsPerPage, page} = tableOptions.value

        try {
            isLoading.value = true
            const rq = {
                ...filters.value,
                ...(filters.value.name && {pattern: filters.value.name}),
            }
            clean(rq)

            if (itemsPerPage !== -1) Object.assign(rq, {limit: itemsPerPage, offset: itemsPerPage * (page - 1)})

            const {shipContents, count} = await app.$api.shipContent.get(rq)
            totalCount.value = count
            ships.value = shipContents
        } catch (e) {
            ships.value = []
        } finally {
            isLoading.value = false
        }
    }

    const useHeaders = computed(() => {
        return [
            {title: t('name'), value: 'name', minWidth: '120px'},
            {title: t('type'), value: 'type'},
            {title: t('year_built'), value: 'yearBuilt'},
            {title: t('cruises.length'), value: 'lengthCm'},
            {title: t('cruises.width'), value: 'widthCm'},
            {title: t('cruises.decks_count'), value: 'decksCount'},
            {title: t('cruises.cabins_count'), value: 'cabinsCount'},
            {title: t('passengers'), value: 'maxPassengers'},
        ]
    })

    const onUpdateTableOptions = (options): void => {
        tableOptions.value = {itemsPerPage: options.itemsPerPage, page: options.page}
        loadData()
    }

    const hasActiveFilters = computed(() => {
        return !isEqual(initShipFilter(), filters.value)
    })

    const resetFilters = (): void => {
        filters.value = initShipFilter()
        loadData()
    }
</script>
